import '../../App.css';
import Swal from 'sweetalert2';
import { useEffect, useState, useCallback, useContext } from 'react';
import {
  getMyBookings,
  deleteBooking,
  completeBooking,
  reduceSession,
  sendNextSessionEmail,
  createGuestBooking,
  getGuestBookings,
  rescheduleGuestSession,
} from '../../api/booking';
import { getServices } from '../../api/service';
import { AuthContext } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const EMPTY_GUEST_FORM = {
  firstName: '',
  lastName: '',
  contactNumber: '',
  service: '',
  scheduledAt: '',
  sessionCount: 1,
  perSessionPrice: '',
  sessionsPaid: 0,
};

function MyBookings() {
  const [bookings, setBookings] = useState([]);
  const [guestBookings, setGuestBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('bookings');
  const [services, setServices] = useState([]);
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [guestForm, setGuestForm] = useState(EMPTY_GUEST_FORM);
  const [guestFormLoading, setGuestFormLoading] = useState(false);

  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  // ── Live amount preview (derived, no state needed) ────────────
  const sessions     = Math.max(1, Number(guestForm.sessionCount) || 1);
  const pricePerSess = Math.max(0, Number(guestForm.perSessionPrice) || 0);
  const paidSessions = Math.min(Math.max(0, Number(guestForm.sessionsPaid) || 0), sessions);
  const totalAmt     = pricePerSess * sessions;
  const paidAmt      = pricePerSess * paidSessions;   // P: sessions paid × price
  const remainingAmt = paidAmt;                        // R: all paid sessions unused at creation

  // ── Fetch regular bookings ────────────────────────────────────
  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const { data } = await getMyBookings();
        setBookings(data);
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Failed to fetch bookings',
          text: error?.response?.data?.message || error.message || 'Something went wrong.',
          timer: 2500,
          showConfirmButton: false,
        });
      }
    };
    fetchBookings();
  }, []);

  // ── Fetch guest bookings + services when admin opens guest tab ─
  useEffect(() => {
    if (user?.role !== 'admin' || activeTab !== 'guests') return;

    const fetchGuests = async () => {
      try {
        const { data } = await getGuestBookings();
        setGuestBookings(data);
      } catch (err) { /* silent */ }
    };

    const fetchServices = async () => {
      try {
        const { data } = await getServices();
        setServices(data);
      } catch (err) { /* silent */ }
    };

    fetchGuests();
    fetchServices();
  }, [activeTab, user]);

  // ── Mark completed ────────────────────────────────────────────
  const handleComplete = useCallback(async (bookingId, isGuest = false) => {
    try {
      await completeBooking(bookingId);
      const updater = (prev) =>
        prev.map((b) => b._id === bookingId ? { ...b, bookingStatus: 'completed' } : b);
      isGuest ? setGuestBookings(updater) : setBookings(updater);
      Swal.fire('✅ Success', 'Booking marked as completed.', 'success');
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || err.message, timer: 2000, showConfirmButton: false });
    }
  }, []);

  // ── Delete ────────────────────────────────────────────────────
  const handleDelete = useCallback(async (bookingId, isGuest = false) => {
    try {
      await deleteBooking(bookingId);
      const remover = (prev) => prev.filter((b) => b._id !== bookingId);
      isGuest ? setGuestBookings(remover) : setBookings(remover);
      Swal.fire({ icon: 'success', title: '🗑️ Deleted', text: 'Booking removed.', timer: 2000, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error deleting', text: err?.response?.data?.message || err.message, timer: 2000, showConfirmButton: false });
    }
  }, []);

  // ── Reschedule guest session ──────────────────────────────────
  const handleRescheduleGuest = useCallback(async (bookingId) => {
    const { value: newDate } = await Swal.fire({
      title: 'Reschedule Guest Session',
      html: `<input type="datetime-local" id="swal-datetime" class="swal2-input" required />`,
      focusConfirm: false,
      preConfirm: () => {
        const input = document.getElementById('swal-datetime');
        if (!input.value) { Swal.showValidationMessage('Please select a date & time'); }
        return input.value;
      },
      showCancelButton: true,
      confirmButtonText: 'Reschedule',
    });
    if (!newDate) return;

    try {
      const { data } = await rescheduleGuestSession(bookingId, newDate);
      setGuestBookings((prev) => prev.map((b) => b._id === bookingId ? data.booking : b));
      Swal.fire({ icon: 'success', title: 'Rescheduled ✅', text: 'Admin notified by email.', timer: 1800, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed to reschedule', text: err?.response?.data?.message || err.message });
    }
  }, []);

  // ── Send next-session email (regular bookings) ────────────────
  const handleSendNextSessionEmail = async (bookingId) => {
    try {
      const { value: nextSession } = await Swal.fire({
        title: 'Select Next Session Date & Time',
        html: `<input type="datetime-local" id="swal-datetime" class="swal2-input" required />`,
        focusConfirm: false,
        preConfirm: () => {
          const input = document.getElementById('swal-datetime');
          if (!input.value) { Swal.showValidationMessage('Please select a date & time first'); }
          return input.value;
        },
        showCancelButton: true,
        confirmButtonText: 'Send Email',
      });
      if (!nextSession) return;

      const { data } = await sendNextSessionEmail(bookingId, nextSession);
      Swal.fire({ icon: 'success', title: 'Email Sent 📧', text: data.message || 'Next session email sent.', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed to send email', text: err?.response?.data?.message || err.message });
    }
  };

  // ── Reduce session ────────────────────────────────────────────
  const handleReduceSession = useCallback(async (bookingId, isGuest = false) => {
    try {
      const { data } = await reduceSession(bookingId);
      const updater = (prev) => prev.map((b) => (b._id === bookingId ? data : b));
      isGuest ? setGuestBookings(updater) : setBookings(updater);
      Swal.fire({
        icon: 'success',
        title: isGuest ? 'Session reduced ✅' : 'Session reduced & Email Sent 📧',
        text: `Remaining Sessions: ${data.remainingSessions}`,
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed to reduce session', text: err?.response?.data?.message || err.message });
    }
  }, []);

  // ── Pay now (client) ──────────────────────────────────────────
  const handleCompletePayment = (booking) => {
    navigate(`/payment/${booking._id}`, { state: { amount: booking.amount } });
  };

  // ── Guest form change ─────────────────────────────────────────
  const handleGuestFormChange = (e) => {
    const { name, value } = e.target;
    setGuestForm((prev) => ({ ...prev, [name]: value }));
  };

  // ── Guest form submit ─────────────────────────────────────────
  const handleGuestSubmit = async (e) => {
    e.preventDefault();
    if (!guestForm.firstName || !guestForm.lastName || !guestForm.service || !guestForm.scheduledAt) {
      Swal.fire({ icon: 'warning', title: 'Missing fields', text: 'Please fill all required fields.' });
      return;
    }
    if (!guestForm.perSessionPrice || Number(guestForm.perSessionPrice) <= 0) {
      Swal.fire({ icon: 'warning', title: 'Missing price', text: 'Please enter the per session price.' });
      return;
    }
    setGuestFormLoading(true);
    try {
      const { data } = await createGuestBooking({
        firstName:       guestForm.firstName,
        lastName:        guestForm.lastName,
        contactNumber:   guestForm.contactNumber,
        service:         guestForm.service,
        scheduledAt:     guestForm.scheduledAt,
        sessionCount:    Number(guestForm.sessionCount),
        perSessionPrice: Number(guestForm.perSessionPrice),
        sessionsPaid:    Number(guestForm.sessionsPaid),
      });
      setGuestBookings((prev) => [data.booking, ...prev]);
      setShowGuestForm(false);
      setGuestForm(EMPTY_GUEST_FORM);
      Swal.fire({ icon: 'success', title: 'Guest Booking Added ✅', timer: 1800, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || err.message });
    } finally {
      setGuestFormLoading(false);
    }
  };

  // ── Tooltip ───────────────────────────────────────────────────
  const Tooltip = ({ booking }) => (
    <div className="tooltip-container">
      {`${booking.firstName} ${booking.lastName}`}
      <div className="tooltip-content">
        <p><strong>Contact:</strong> {booking.contactNumber}</p>
        <p><strong>Email:</strong> {booking.emailId}</p>
        <p><strong>Region:</strong> {booking.countryOrRegion}</p>
      </div>
    </div>
  );

  const upcomingSchedules = bookings
    .filter((b) => b.scheduledAt && new Date(b.scheduledAt) > new Date() && b.remainingSessions > 0)
    .sort((a, b) => new Date(a.scheduledAt) - new Date(b.scheduledAt));

  return (
    <>
      {/* ── Tab bar (admin only) ──────────────────────────────── */}
      {user?.role === 'admin' && (
        <div className="bookings-tab-bar">
          <button
            className={`tab-btn ${activeTab === 'bookings' ? 'tab-btn--active' : ''}`}
            onClick={() => setActiveTab('bookings')}
          >
            Client Bookings
          </button>
          <button
            className={`tab-btn ${activeTab === 'guests' ? 'tab-btn--active' : ''}`}
            onClick={() => setActiveTab('guests')}
          >
            Guest Bookings
          </button>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          TAB 1 — REGULAR BOOKINGS
      ══════════════════════════════════════════════════════════ */}
      {activeTab === 'bookings' && (
        <div className="MyBookings-wrapper">
          <h2 className="MyBookings-title">My Bookings</h2>
          <div className="table-container">
            <table className="MyBookings-table">
              <thead>
                <tr>
                  {user?.role === 'admin' && <th>Name</th>}
                  <th>Service</th>
                  <th>Booking Date</th>
                  <th>Scheduled At</th>
                  <th>Payment</th>
                  <th>Sessions</th>
                  {user?.role === 'admin' && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {bookings.map((booking) => (
                  <tr key={booking._id}>
                    {user?.role === 'admin' ? <td><Tooltip booking={booking} /></td> : null}
                    <td>{booking.service ? booking.service.name : 'N/A'}</td>
                    <td>{new Date(booking.createdAt).toLocaleString('en-IN')}</td>
                    <td>
                      {booking.scheduledAt
                        ? new Date(booking.scheduledAt).toLocaleString('en-IN')
                        : 'Not Scheduled'}
                    </td>
                    <td>
                      {booking.paymentStatus === 'paid' ? (
                        <>
                          <div><strong>A_P:</strong> {booking.paidAmount}</div>
                          <div><strong>A_R:</strong> {booking.remainingAmount}</div>
                        </>
                      ) : (
                        <>
                          <span>Pending</span>
                          {user?.role === 'client' && (
                            <button className="btn-payment" onClick={() => handleCompletePayment(booking)}>
                              Pay Now
                            </button>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      <div><strong>S_B:</strong> {booking.sessionCount}</div>
                      <div><strong>S_R:</strong> {booking.remainingSessions}</div>
                    </td>
                    {user?.role === 'admin' && (
                      <td>
                        <button className="btn-reduce" onClick={() => handleReduceSession(booking._id, false)}>—</button>
                        <button className="btn-complete" onClick={() => handleComplete(booking._id, false)} disabled={booking.bookingStatus === 'completed'}>
                          {booking.bookingStatus === 'completed' ? '🗸 Completed' : '🗸'}
                        </button>
                        <button className="btn-mail" onClick={() => handleSendNextSessionEmail(booking._id)} disabled={booking.remainingSessions === 0}>🕮</button>
                        <button className="btn-delete" onClick={() => handleDelete(booking._id, false)}>🗑</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          TAB 2 — GUEST BOOKINGS (admin only)
      ══════════════════════════════════════════════════════════ */}
      {activeTab === 'guests' && user?.role === 'admin' && (
        <div className="MyBookings-wrapper">
          <div className="guest-header">
            <h2 className="MyBookings-title">Guest Bookings</h2>
            <button className="btn-add-guest" onClick={() => setShowGuestForm((v) => !v)}>
              {showGuestForm ? '✕ Cancel' : '+ Add Guest Booking'}
            </button>
          </div>

          {/* ── Add Guest Form ──────────────────────────────── */}
          {showGuestForm && (
            <form className="guest-form" onSubmit={handleGuestSubmit}>
              <div className="guest-form-grid">

                <div className="guest-form-field">
                  <label>First Name *</label>
                  <input name="firstName" value={guestForm.firstName} onChange={handleGuestFormChange} placeholder="First name" required />
                </div>

                <div className="guest-form-field">
                  <label>Last Name *</label>
                  <input name="lastName" value={guestForm.lastName} onChange={handleGuestFormChange} placeholder="Last name" required />
                </div>

                <div className="guest-form-field">
                  <label>Contact Number</label>
                  <input name="contactNumber" value={guestForm.contactNumber} onChange={handleGuestFormChange} placeholder="Phone number" />
                </div>

                <div className="guest-form-field">
                  <label>Service *</label>
                  <select name="service" value={guestForm.service} onChange={handleGuestFormChange} required>
                    <option value="">Select service</option>
                    {services.map((s) => (
                      <option key={s._id} value={s._id}>{s.name}</option>
                    ))}
                  </select>
                </div>

                <div className="guest-form-field">
                  <label>Scheduled At *</label>
                  <input type="datetime-local" name="scheduledAt" value={guestForm.scheduledAt} onChange={handleGuestFormChange} required />
                </div>

                <div className="guest-form-field">
                  <label>Sessions Booked</label>
                  <input type="number" name="sessionCount" value={guestForm.sessionCount} onChange={handleGuestFormChange} min="1" />
                </div>

                <div className="guest-form-field">
                  <label>Per Session Price (₹) *</label>
                  <input type="number" name="perSessionPrice" value={guestForm.perSessionPrice} onChange={handleGuestFormChange} min="0" placeholder="e.g. 1500" required />
                </div>

                <div className="guest-form-field">
                  <label>Sessions Paid</label>
                  <input type="number" name="sessionsPaid" value={guestForm.sessionsPaid} onChange={handleGuestFormChange} min="0" max={sessions} placeholder="How many sessions paid" />
                </div>

              </div>

              {/* Live amount preview */}
              {pricePerSess > 0 && (
                <div className="guest-amount-preview">
                  <span>Total: <strong>₹{totalAmt}</strong></span>
                  <span>Paid: <strong>₹{paidAmt}</strong> ({paidSessions}/{sessions} sessions)</span>
                  <span>Remaining: <strong>₹{remainingAmt}</strong></span>
                </div>
              )}

              <button className="btn-guest-submit" type="submit" disabled={guestFormLoading}>
                {guestFormLoading ? 'Saving...' : 'Save Guest Booking'}
              </button>
            </form>
          )}

          {/* ── Guest Table ──────────────────────────────────── */}
          <div className="table-container">
            <table className="MyBookings-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Service</th>
                  <th>Scheduled At</th>
                  <th>Sessions (B / R)</th>
                  <th>Per Session</th>
                  <th>Amount (P / R)</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {guestBookings.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>
                      No guest bookings yet. Click "+ Add Guest Booking" to create one.
                    </td>
                  </tr>
                ) : (
                  guestBookings.map((booking) => (
                    <tr key={booking._id}>
                      <td>{booking.firstName} {booking.lastName}</td>
                      <td>{booking.contactNumber || '—'}</td>
                      <td>{booking.service ? booking.service.name : 'N/A'}</td>
                      <td>{booking.scheduledAt ? new Date(booking.scheduledAt).toLocaleString('en-IN') : '—'}</td>
                      <td>
                        <div><strong>B:</strong> {booking.sessionCount}</div>
                        <div><strong>R:</strong> {booking.remainingSessions}</div>
                      </td>
                      <td>₹{booking.perSessionPrice ?? '—'}</td>
                      <td>
                        <div><strong>P:</strong> ₹{booking.paidAmount}</div>
                        <div><strong>R:</strong> ₹{booking.remainingAmount}</div>
                      </td>
                      <td>
                        <button className="btn-reduce" title="Reduce session" onClick={() => handleReduceSession(booking._id, true)}>—</button>
                        <button className="btn-complete" title="Mark complete" onClick={() => handleComplete(booking._id, true)} disabled={booking.bookingStatus === 'completed'}>
                          {booking.bookingStatus === 'completed' ? '🗸' : '🗸'}
                        </button>
                        <button className="btn-mail" title="Reschedule session" onClick={() => handleRescheduleGuest(booking._id)} disabled={booking.remainingSessions === 0}>📅</button>
                        <button className="btn-delete" title="Delete" onClick={() => handleDelete(booking._id, true)}>🗑</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Upcoming Sessions (regular bookings, admin only) ────── */}
      {activeTab === 'bookings' && user?.role === 'admin' && upcomingSchedules.length > 0 && (
        <div className="upcoming-wrapper">
          <h3 className="upcoming-title">Upcoming Sessions</h3>
          <div className="upcoming-list">
            {upcomingSchedules.map((booking) => (
              <div key={booking._id} className="upcoming-card">
                <div className="upcoming-left">
                  <div className="upcoming-info">
                    <strong>{booking.firstName} {booking.lastName}</strong>
                    <span>{booking.service?.name}</span>
                  </div>
                  <div className="upcoming-date">{new Date(booking.scheduledAt).toLocaleString('en-IN')}</div>
                </div>
                <div className="upcoming-right">
                  {booking.meetLink ? (
                    <a href={booking.meetLink} target="_blank" rel="noopener noreferrer" className="btn-join">Start session</a>
                  ) : (
                    <span className="meet-na">No Meet</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default MyBookings;
